import _ from 'lodash'

import type { SelectedFilterOption } from 'features/charts-v2/types'
import { isYogiQLStoredPanel } from 'features/charts-v2/types/stored-panel'
import type {
  DashboardItem,
  DashboardResponse,
  DashboardSettings,
} from 'features/dashboards-v2/types'
import type { DashboardFilterStateV2 } from 'features/dashboards-v2/types/filters-v2'
import { parseKeywordInput } from 'features/dashboards-v2/utils/parse-keyword-input'
import { isYogiQLStoredFilterState } from 'features/dashboards-v2/utils/stored-widget-filters'

// Older saved dashboards stored a pasted keyword list as a single string in a
// `searchCriteria` operator group (e.g. `'Includes some': ['"plastic", "PET"']`),
// which matches nothing at query time. The live keyword panel now splits input
// on entry, but that never touches already-saved data. This normalizes those
// stored arrays on read — applied at the dashboard service response boundary so
// every downstream consumer (filter hydration, per-item embeddable filters, the
// editor, and the executed query, which is built from that runtime state) sees
// split keywords. Idempotent: already-split data is returned unchanged.
//
// And drops `null`-valued optional keys from every stored filter selection
// (mirrors 2.1's copy of this normalizer). The API sends
// `"min":null,"max":null,"valueType":null,"numericValue":null` on every
// filter, but `SelectedFilterOption` declares them `number | undefined`, and
// every downstream "is this a range / numeric filter?" check tests
// `!== undefined` — true for `null`. Left in, an `is` filter on a value list
// reads as a range: the filter pill's hover shows the bare field name
// ("brand") and counts it as one value, and a tile's picker restriction
// becomes a range (PROD-6254). At this boundary it covers every response —
// loads AND the save response written back into the detail caches.

const splitTerms = (terms: string[]): string[] =>
  Array.from(new Set(terms.flatMap(parseKeywordInput)))

const normalizeSearchCriteria = (
  searchCriteria: Record<string, string[]>,
): Record<string, string[]> => {
  const next: Record<string, string[]> = {}
  for (const [operator, terms] of Object.entries(searchCriteria)) {
    next[operator] = splitTerms(terms)
  }
  return next
}

const dropNullFields = (
  selection: SelectedFilterOption,
): SelectedFilterOption =>
  Object.values(selection).some((value) => value === null)
    ? (_.omitBy(selection, _.isNull) as SelectedFilterOption)
    : selection

const normalizeFilters = (
  filters: DashboardFilterStateV2,
): DashboardFilterStateV2 => {
  if (!filters) return filters
  const hasSearchCriteria =
    !!filters.searchCriteria && Object.keys(filters.searchCriteria).length > 0
  return {
    ...filters,
    ...(Array.isArray(filters.filters) && {
      filters: filters.filters.map(dropNullFields),
    }),
    ...(hasSearchCriteria && {
      searchCriteria: normalizeSearchCriteria(filters.searchCriteria),
    }),
  }
}

const normalizeV2State = (
  v2State: NonNullable<DashboardItem['v2State']>,
): NonNullable<DashboardItem['v2State']> => ({
  ...v2State,
  // A YogiQL panel has no `filters`; `readStoredDashboard` decodes it.
  panels: (v2State.panels ?? []).map((panel) =>
    isYogiQLStoredPanel(panel)
      ? panel
      : { ...panel, filters: (panel.filters ?? []).map(dropNullFields) },
  ),
})

const normalizeItem = (item: DashboardItem): DashboardItem => ({
  ...item,
  // A YogiQL widget state has no `filters`; `readStoredDashboard` decodes it.
  filterValues: isYogiQLStoredFilterState(item.filterValues)
    ? item.filterValues
    : normalizeFilters(item.filterValues),
  ...(item.v2State && { v2State: normalizeV2State(item.v2State) }),
})

const normalizeSettings = (settings: DashboardSettings): DashboardSettings => ({
  ...settings,
  filters: normalizeFilters(settings.filters),
  items: (settings.items ?? []).map(normalizeItem),
})

export const normalizeDashboardResponse = (
  response: DashboardResponse,
): DashboardResponse => {
  if (!response?.dashboard?.dashboard_settings) return response
  return {
    ...response,
    dashboard: {
      ...response.dashboard,
      dashboard_settings: normalizeSettings(
        response.dashboard.dashboard_settings,
      ),
    },
  }
}
